/**
 * A membership bought on the membership-first flow (book.aheadhealth.com) is
 * booked there too: a booking-app link carrying its token forwards to the
 * membership-first app with the same token and language. Legacy memberships (no flow)
 * stay here. So does a membership-first one whose baseline is booked or done:
 * the membership-first app books the baseline only, and the follow-ups (the
 * follow-up reminder emails link here) are booked here like any member's.
 *
 * The membership-first app's address per environment: VITE_MEMBERSHIP_FIRST_URL when
 * set, production's own on booking.aheadhealth.com, the membership-first
 * dev preview everywhere else.
 */
import type { MembershipData } from "@/services/membershipService";

export const MEMBERSHIP_FIRST_PRODUCTION_URL = "https://book.aheadhealth.com";
export const MEMBERSHIP_FIRST_PREVIEW_URL =
  "https://membership-first-git-dev-ahead-healths-projects.vercel.app";

export const membershipFirstBaseUrl = (
  configured: string | undefined,
  hostname: string,
): string => {
  if (configured) return configured;
  return hostname === "booking.aheadhealth.com"
    ? MEMBERSHIP_FIRST_PRODUCTION_URL
    : MEMBERSHIP_FIRST_PREVIEW_URL;
};

/** Where a membership-first membership is forwarded to, or null to stay. */
export const membershipFirstRedirectUrl = (
  membership: Pick<MembershipData, "flow" | "membership_entitlements">,
  from: {
    token: string;
    locale: string | null;
    membershipDomain: string | null;
  },
  baseUrl: string,
): string | null => {
  if (membership.flow !== "membership-first") return null;
  const baseline = membership.membership_entitlements.find(
    (e) =>
      e.entitlement_type === "baseline_blood_test" && e.billing_cycle === 1,
  );
  if (baseline?.status === "booked" || baseline?.status === "completed")
    return null;
  const url = new URL(baseUrl);
  url.searchParams.set("membershipToken", from.token);
  if (from.locale) url.searchParams.set("locale", from.locale);
  if (from.membershipDomain)
    url.searchParams.set("membershipDomain", from.membershipDomain);
  return url.toString();
};
